import { CustomKpi } from 'common/types/customKpi';
import { FilterOption, FilterOptionType } from 'common/types/filters';
import { KPI, KPIDisplayFormat, KPIGroups, KPIType } from 'common/types/kpi';
import { UNDEFINED_TYPE } from 'settings/constants';

export const createKpiMap = (kpis: KPI[]): Record<string, string> => {
  const kpisMap: Record<string, string> = {};

  kpis.forEach(kpi => {
    const { display_format, kpi_identifier, kpi_label, question_options, kpi_formulas } = kpi;

    // Options and formulas are labelled regardless of display_format: the backend forces "bar" for
    // funnel KPIs (e.g. brand-usage) even when a study asks them as a multi-option question
    question_options?.forEach(option => {
      kpisMap[option.option_identifier] = `${kpi_label}: ${option.option_label}`;
    });

    kpi_formulas?.forEach(formula => {
      kpisMap[formula.kpi_identifier] = `${kpi_label}: ${formula.kpi_label}`;
    });

    if (display_format !== KPIDisplayFormat.LIST || !(question_options || kpi_formulas)) {
      kpisMap[kpi_identifier] = kpi_label;
    }
  });

  return kpisMap;
};

export const createCustomKpisMap = (customKpis: CustomKpi[]): Record<string, CustomKpi> =>
  Object.fromEntries(customKpis.map(customKpi => [customKpi.identifier, customKpi]));

// The custom KPIs among a chart's kpi_option_identifiers, in selection order
export const getSelectedCustomKpis = (kpiIdentifiers: string[], customKpisMap: Record<string, CustomKpi>): CustomKpi[] =>
  kpiIdentifiers.flatMap(id => customKpisMap[id] ?? []);

// Custom KPI names are labelled as-is, without a "<KPI>: " prefix: they are not options of a single KPI
export const createCustomKpiLabelMap = (customKpis: CustomKpi[]): Record<string, string> =>
  Object.fromEntries(customKpis.map(({ identifier, name }) => [identifier, name]));

const KPIsWeight: Record<string, number> = {
  'unaided-brand-awareness': 0,
  'aided-brand-awareness': 1,
  'brand-consideration': 2,
  'brand-preference': 3,
  'brand-usage': 4,
  'ad-awareness': 5,
  'brand-associations': 6,
};

export const sortKPIs = (kpis: KPI[]): KPI[] => {
  // remove brand score: https://trello.com/c/RXBi5WUA/86-hide-brandscore-kpi-from-the-chart-form
  kpis = kpis.filter(kpi => kpi.kpi_identifier !== 'brand-score');
  // will be sorted by hardcoded order
  const defaultKPIs = kpis
    .filter(({ kpi_identifier }) => typeof KPIsWeight[kpi_identifier] !== UNDEFINED_TYPE)
    .sort((kpiA, kpiB) => {
      const weightA = KPIsWeight[kpiA.kpi_identifier] ?? kpis.length;
      const weightB = KPIsWeight[kpiB.kpi_identifier] ?? kpis.length;
      return weightA - weightB;
    });

  // will be sorted alphabetically
  const extraKPIs = kpis
    .filter(({ kpi_identifier }) => typeof KPIsWeight[kpi_identifier] === UNDEFINED_TYPE)
    .sort((kpiA, kpiB) => {
      return kpiA.kpi_label < kpiB.kpi_label ? -1 : 1;
    });

  return defaultKPIs.concat(extraKPIs).map(kpi => {
    if (kpi.question_options) {
      return {
        ...kpi,
        question_options: [...kpi.question_options].sort((optA, optB) => {
          const useAlphabetical = optA.option_order === null;

          if (useAlphabetical) {
            return optA.option_label < optB.option_label ? -1 : 1;
          } else {
            const weightA = optA.option_order ?? 0;
            const weightB = optB.option_order ?? 0;

            return weightA - weightB;
          }
        }),
      };
    } else {
      return kpi;
    }
  });
};

export const groupKpisByType = (kpis: KPI[]): KPIGroups => {
  const kpisGroups: KPIGroups = { percentage: [], absolute: [] };

  kpis.forEach(kpi => {
    if (kpi.display_format === KPIDisplayFormat.LIST) {
      kpi.question_options?.forEach(option => {
        kpisGroups.percentage.push(option.option_identifier);
      });

      kpi.kpi_formulas?.forEach(formula => {
        if (formula.value_type === KPIType.PERCENTAGE) {
          kpisGroups.percentage.push(formula.kpi_identifier);
        } else {
          kpisGroups.absolute.push(formula.kpi_identifier);
        }
      });
    } else {
      kpisGroups.percentage.push(kpi.kpi_identifier);
    }
  });

  return kpisGroups;
};

export const filterKpisByType = (
  kpisList: FilterOption[] | undefined,
  switchType: KPIType,
  kpisTypeGroups: Record<KPIType, Array<string | number>>,
): FilterOption[] => {
  if (switchType === KPIType.ABSOLUTE) {
    const filtered = [] as FilterOption[];
    kpisList?.forEach(kpiOption => {
      if (kpiOption.type === FilterOptionType.OPTION) {
        const availableOptions = (kpiOption || [])?.options?.filter(({ value }) => kpisTypeGroups.absolute.includes(value));
        if (Array.isArray(availableOptions) && availableOptions.length) {
          filtered.push({ ...kpiOption, options: availableOptions });
        }
      }
    });

    return filtered;
  } else {
    const filtered = [] as FilterOption[];
    kpisList?.forEach(kpiOption => {
      if (kpiOption.type === FilterOptionType.OPTION) {
        filtered.push(kpiOption);
      } else if (kpiOption.type === FilterOptionType.GROUP) {
        const availableOptions = kpiOption.options.filter(({ value }) => !kpisTypeGroups.absolute.includes(value));
        if (availableOptions.length) {
          filtered.push({ ...kpiOption, options: availableOptions });
        }
      }
    });

    return filtered;
  }
};
